import { useQuery } from "@tanstack/react-query";
import type { CycleLeadCountsResponse } from "@school-sales-ai/shared";
import { api } from "@/lib/api";

/**
 * Contextual filters that shape the cycle counts. Never includes `cycleIds` —
 * counts are grouped across all cycles. Keeps `productIds` so the counts stay
 * predictive when a product filter is active. Lists are comma-joined.
 */
export type CycleCountFilters = {
  stages?: string;
  channel?: string;
  scoreMin?: string;
  scoreMax?: string;
  productIds?: string;
  search?: string;
  from?: string;
  to?: string;
  // Comma-joined label UUIDs — keeps cycle counts predictive under a label filter.
  labelIds?: string;
  // Comma-joined assignee ids («Asignado a»), "none" = unassigned.
  assigneeIds?: string;
};

export function useCycleLeadCounts(
  filters: CycleCountFilters,
): ReturnType<typeof useQuery<CycleLeadCountsResponse>> {
  const entries = Object.entries(filters).filter(
    ([, v]) => v !== "" && v !== undefined,
  );
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();

  return useQuery<CycleLeadCountsResponse>({
    queryKey: ["cycle-counts", filters],
    queryFn: () =>
      api.get<CycleLeadCountsResponse>(
        `/crm/leads/cycle-counts${qs ? `?${qs}` : ""}`,
      ),
    staleTime: 60_000,
  });
}
